import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import AiConfigForm from '../components/ai-config/AiConfigForm';
import TenantSelect from '../components/ai-config/TenantSelect';
import LoadingSpinner from '../components/common/LoadingSpinner';
import useTenantAiConfig from '../hooks/useTenantAiConfig';
import useTenants from '../hooks/useTenants';

/**
 * Seite "KI-Konfiguration": OpenRouter-API-Key, Modell und
 * Lagebeschreibungs-Prompt je Mandant pflegen.
 *
 * Ist genau ein Mandant vorhanden, wird er automatisch vorausgewaehlt
 * (kein sinnvoller Leerzustand bei nur einem Mandanten). Bei mehreren
 * Mandanten muss aktiv gewaehlt werden, damit nicht versehentlich die
 * Konfiguration des FALSCHEN Mandanten bearbeitet wird.
 */
function AiConfigPage(): React.JSX.Element {
  const tenants = useTenants();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Genau ein Mandant -> automatisch vorauswaehlen. Nur wirksam, solange
  // noch keine (andere) Auswahl getroffen wurde.
  useEffect(() => {
    if (tenants.data !== null && tenants.data.length === 1 && selectedTenantId === null) {
      setSelectedTenantId(tenants.data[0].id);
    }
  }, [tenants.data, selectedTenantId]);

  /**
   * Nach einem erneuten Laden (z. B. "Erneut laden") kann der zuvor
   * gewaehlte Mandant in der aktualisierten Liste fehlen (geloescht).
   * Die Auswahl wird dann verworfen, statt eine KI-Konfiguration fuer
   * eine nicht mehr existierende ID anzuzeigen.
   */
  useEffect(() => {
    if (
      tenants.data !== null &&
      selectedTenantId !== null &&
      !tenants.data.some((tenant) => tenant.id === selectedTenantId)
    ) {
      setSelectedTenantId(null);
    }
  }, [tenants.data, selectedTenantId]);

  const aiConfig = useTenantAiConfig(selectedTenantId);

  return (
    <div id="ai-config-page">
      <h1 id="ai-config-heading" className="page-heading h3 mb-1">
        KI-Konfiguration
      </h1>
      <p id="ai-config-subheading" className="text-muted mb-3">
        OpenRouter-Zugang, Lagebeschreibungs-Prompt, Reasoning und Websuche je Mandant
      </p>

      <div id="ai-config-content" style={{ maxWidth: '900px' }}>
        {tenants.loading && (
          <div id="ai-config-tenants-loading-container" className="d-flex justify-content-center py-4">
            <LoadingSpinner id="ai-config-tenants-loading" />
          </div>
        )}

        {!tenants.loading && tenants.error !== null && (
          <Alert id="ai-config-tenants-error" variant="danger">
            {tenants.error}
            <div className="mt-2">
              <Button id="ai-config-reload-button" variant="outline-danger" size="sm" onClick={tenants.reload}>
                Erneut laden
              </Button>
            </div>
          </Alert>
        )}

        {!tenants.loading && tenants.error === null && tenants.data !== null && tenants.data.length === 0 && (
          <Alert id="ai-config-tenants-empty" variant="info">
            Es sind keine Mandanten vorhanden.
          </Alert>
        )}

        {!tenants.loading && tenants.error === null && tenants.data !== null && tenants.data.length > 0 && (
          <>
            <TenantSelect
              tenants={tenants.data}
              selectedTenantId={selectedTenantId}
              onChange={setSelectedTenantId}
              disabled={aiConfig.saving || aiConfig.deleting}
            />

            {selectedTenantId === null && (
              <p id="ai-config-no-selection" className="text-muted">
                Bitte einen Mandanten auswählen.
              </p>
            )}

            {selectedTenantId !== null && (
              <Card id="ai-config-card" className="shadow-sm">
                <Card.Body id="ai-config-card-body">
                  <AiConfigForm
                    config={aiConfig.data}
                    loading={aiConfig.loading}
                    saving={aiConfig.saving}
                    deleting={aiConfig.deleting}
                    error={aiConfig.error}
                    successMessage={aiConfig.successMessage}
                    onSave={aiConfig.save}
                    onDelete={aiConfig.remove}
                    onClearMessages={aiConfig.clearMessages}
                  />
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AiConfigPage;
